// src/encounter/services/use-save-immunization.ts
// Rail-scoped save hook for the IPS Immunizations/Vaccines section (PROD-1267). Wraps the
// curl-verified Immunization endpoints (POST /api/Patients/CreateImmunization, PUT
// /api/Patients/immunization/{id}) and invalidates BOTH the ENCOUNTER summary query
// (['patient-summary', patientId] — the rail render source) AND the full chart list
// (['patient-immunizations', patientId] — the source the rail looks edit/delete records up from).
// Mirrors use-save-procedure / use-save-condition: transport + cache only; body assembly +
// wire-shape rules live in lib/to-immunization-request.ts.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post, put } from '@/shared/api/api-client';
import type { ImmunizationRequest } from '@/encounter/lib/to-immunization-request';

export function useSaveImmunization(patientId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<string, Error, ImmunizationRequest>({
    mutationFn: (req: ImmunizationRequest) => {
      if (req.kind === 'create') {
        return post<string>('/api/Patients/CreateImmunization', req.body);
      }
      // update path covers EDIT and soft-DELETE.
      return put<string>(`/api/Patients/immunization/${req.immunizationId}`, req.body);
    },
    onSuccess: () => {
      // Invalidate the summary (rail render source) AND the full chart list the rail sources
      // edit/delete from (usePatientImmunizations, ['patient-immunizations', patientId]). Without
      // the second key a freshly-added/edited row's full record stays stale and its affordance
      // can't complete the round-trip (mirrors use-save-procedure).
      qc.invalidateQueries({ queryKey: ['patient-summary', patientId] });
      qc.invalidateQueries({ queryKey: ['patient-immunizations', patientId] });
    },
  });
}
